import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import sanitize from 'sanitize-html'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { TranslationMode } from 'cng-common/features/translations/configs'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'

import { IPlanningSmbGuidedTranslation } from '../../../configs/guidedWizardTranslations'

export type TGuidedTranslationParams = Record<string, string | number>

export const usePlanningSmbGuidedTranslation = () => {
  const t = useSafetyTranslation()
  return useCallback(
    (text: IPlanningSmbGuidedTranslation, params: TGuidedTranslationParams = {}) => {
      const translated = t(text.key, { defaultValue: text.fallback })
      const template =
        translated === text.key || !translated.trim() || translated === '<p><br></p>'
          ? text.fallback
          : translated
      const plainTemplate = /[<&]/.test(template)
        ? new DOMParser().parseFromString(sanitize(template), 'text/html').body.textContent || ''
        : template
      return plainTemplate.replace(/\{([^{}]+)\}/g, (token, name: string) =>
        params[name] === undefined ? token : String(params[name])
      )
    },
    [t]
  )
}

/** Keep the existing translation editor, and pass only a stable key to Translation. */
export const PlanningSmbGuidedText = ({
  text,
  params = {},
}: {
  text: IPlanningSmbGuidedTranslation
  params?: TGuidedTranslationParams
}) => {
  const mode = useSelector(selectTranslationMode)
  const t = useSafetyTranslation()
  const translate = usePlanningSmbGuidedTranslation()
  const replaceParams = Object.entries(params).map(([key, value]) => ({
    key: `{${key}}`,
    value: String(value),
  }))
  if (mode === TranslationMode.Edit) {
    return (
      <span
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <Translation span text={text.key} replaceParams={replaceParams} />
      </span>
    )
  }
  const translated = t(text.key)
  if (translated !== text.key && translated.trim() && translated !== '<p><br></p>') {
    return <Translation span text={text.key} replaceParams={replaceParams} />
  }
  return <span>{translate(text, params)}</span>
}
